import { ReactNode, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Highlight, Snippet } from 'react-instantsearch-dom'
import { AlgoliaArticle } from 'types'
import { getInitials } from 'utils'
import Avatar from 'common/Avatar/Avatar'
import Styles from './ResultItem.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useModals } from 'almanac-ui/Modal'
import { useButton } from '@react-aria/button'
import { useFocusRing } from '@react-aria/focus'
import { mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import { LastEditedAt } from './LastEditedAt'
import { Text } from 'blaze-ui/Text/Text'

interface Props {
  icon: ReactNode
  preview: ReactNode
  type: string
  item: AlgoliaArticle
}

export const ResultItem = ({ item, icon, type, preview }: Props) => {
  const { closeAllModals } = useModals()
  const { url, creator_image_url, creator_name, last_edited_at } = item
  const ref = useRef<HTMLDivElement>(null)
  const history = useHistory()

  const handleClick = () => {
    history.push(url || '')
    closeAllModals()
  }
  const { buttonProps, isPressed } = useButton({ elementType: 'div', onPress: handleClick }, ref)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <div
      className={cx(Styles.searchResult, {
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocusVisible,
      })}
      ref={ref}
      {...mergeProps(buttonProps, focusProps)}
    >
      {preview}
      <div className={Styles.searchResultInfo}>
        <div className={Styles.top}>
          <div className={Styles.header}>
            <div className={Styles.title} data-testid={item.title}>
              <Heading level={5}>
                <Highlight attribute="title" tagName="span" hit={item} />
              </Heading>
            </div>
            <Text variant="secondary">{type}</Text>
          </div>

          <div className={Styles.summary}>
            <Snippet attribute="plain_text" tagName="span" hit={item} />
          </div>
        </div>
        <div className={Styles.footer}>
          <div className={Styles.user}>
            <Avatar url={creator_image_url} label={getInitials(creator_name)} size={24} />
            <div className={Styles.creatorEditedAt}>
              <Text variant="secondary">
                <LastEditedAt date={last_edited_at} label="Edited" />
              </Text>
            </div>
          </div>
          <div className={Styles.icon}>{icon}</div>
        </div>
      </div>
    </div>
  )
}
